import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const OPTIONAL_NAMES = [
  "William",
  "James",
  "Benjamin",
  "Lucas",
  "Henry",
  "Alexander",
  "Michael",
  "Ethan",
  "Daniel",
  "Matthew",
  "Aiden",
  "Joseph",
  "Samuel",
  "David",
  "Wyatt",
  "Carter",
  "Julian",
  "Luke",
  "Grayson",
  "Isaac",
  "Jayden",
  "Theodore",
  "Oliver",
  "Noah",
  "Liam",
  "Mason",
  "Elijah",
  "Logan",
  "Jackson",
  "Sebastian",
];

function useTypingPlaceholder(
  names: string[],
  typingSpeed = 100,
  deletingSpeed = 50,
  pauseDuration = 1500,
) {
  const [text, setText] = useState("");
  const [nameIndex, setNameIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const currentName = names[nameIndex];

    if (isDeleting) {
      if (text === "") {
        setIsDeleting(false);
        setNameIndex((prev) => (prev + 1) % names.length);
      } else {
        timer = setTimeout(() => {
          setText(text.slice(0, -1));
        }, deletingSpeed);
      }
    } else {
      if (text === currentName) {
        timer = setTimeout(() => {
          setIsDeleting(true);
        }, pauseDuration);
      } else {
        timer = setTimeout(() => {
          setText(currentName.slice(0, text.length + 1));
        }, typingSpeed);
      }
    }

    return () => clearTimeout(timer);
  }, [
    text,
    isDeleting,
    nameIndex,
    names,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
  ]);

  return text;
}

interface PersonalizeStepProps {
  value: string;
  ageConfirmed: boolean;
  marketingAccepted: boolean;
  onChange: (value: string) => void;
  onAgeConfirmedChange: (value: boolean) => void;
  onMarketingAcceptedChange: (value: boolean) => void;
  onNext: () => void;
}

export function PersonalizeStep({
  value,
  ageConfirmed,
  marketingAccepted,
  onChange,
  onAgeConfirmedChange,
  onMarketingAcceptedChange,
  onNext,
}: PersonalizeStepProps) {
  const placeholderName = useTypingPlaceholder(OPTIONAL_NAMES);

  return (
    <div className="max-w-xl mx-auto w-full">
      <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
        Help us personalize your experience
      </h1>

      <div className="flex flex-col mt-8 space-y-8">
        <div className="space-y-2">
          <label className="text-sm border-none font-medium text-foreground">
            What&apos;s your name?{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </label>
          <Input
            placeholder={placeholderName}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-base transition-colors py-6 rounded-xl"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && ageConfirmed) onNext();
            }}
          />
        </div>

        {/* <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            What&apos;s your preferred language?
          </label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-full text-base py-6 rounded-xl">
              <SelectValue placeholder="Select a language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">
                <span className="flex items-center gap-2">🇺🇸 English</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div> */}

        <div className="space-y-6 pt-2">
          <div className="flex space-x-3 items-start relative">
            <Checkbox
              id="age"
              checked={ageConfirmed}
              onCheckedChange={(checked) => onAgeConfirmedChange(checked === true)}
              className="mt-1 w-5 h-5 rounded-md"
            />
            <Label
              htmlFor="age"
              className="font-normal text-[15px] leading-relaxed flex-1 cursor-pointer select-none"
            >
              By checking this box, you confirm you have reached the age of 18
              years old (or the age of legal majority where you live).
            </Label>
          </div>

          <div className="flex space-x-3 items-start relative">
            <Checkbox
              id="marketing"
              checked={marketingAccepted}
              onCheckedChange={(checked) =>
                onMarketingAcceptedChange(checked === true)
              }
              className="mt-1 w-5 h-5 rounded-md"
            />
            <Label
              htmlFor="marketing"
              className="font-normal text-[15px] leading-relaxed flex-1 cursor-pointer select-none"
            >
              I want to receive updates, special offers, and promotional emails
              and understand that I can opt out at any time
            </Label>
          </div>
        </div>
      </div>

      <Button
        onClick={onNext}
        disabled={!ageConfirmed}
        size="lg"
        className="mt-10 rounded-[12px] bg-black hover:bg-black/90 text-white dark:bg-white dark:hover:bg-white/90 dark:text-black font-medium cursor-pointer disabled:cursor-not-allowed"
      >
        Next
      </Button>
    </div>
  );
}
